import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog";
import { format, isSameDay } from "date-fns";

/**
 * Formats a time log date label without time, taking into account logs spanning multiple days or months.
 * Note: it's supposed to be used with settled logs only (with `endDate`).
 */
export function formatTimeLogDateLabel<
  TTimeLog extends Pick<TaskTimeLogModel, "startDate" | "endDate">,
>({ startDate, endDate }: TTimeLog): string {
  // Safe check to ensure endDate is not null
  endDate = endDate ?? new Date();

  if (isSameDay(startDate, endDate)) {
    return format(startDate, "MMM d");
  }

  const startDateStr = format(startDate, "MMM d");
  const endDateStr = format(endDate, "MMM d");
  return `${startDateStr} - ${endDateStr}`;
}

/**
 * Formats a time log date and time label, taking into account logs spanning multiple days or months.
 * Note: it's supposed to be used with settled logs only (with `endDate`).
 */
export function formatTimeLogDateTimeLabel<
  TTimeLog extends Pick<TaskTimeLogModel, "startDate" | "endDate">,
>(timeLog: TTimeLog): string {
  const { startDate, endDate: rawEndDate } = timeLog;
  // Safe check to ensure endDate is not null
  const endDate = rawEndDate ?? new Date();

  const startTime = format(startDate, "h:mm a");
  const endTime = format(endDate, "h:mm a");

  const dateLabel = formatTimeLogDateLabel(timeLog);

  if (isSameDay(startDate, endDate)) {
    return `${dateLabel}, ${startTime} - ${endTime}`;
  }

  // For multi-day logs, insert times after each date
  const startDateStr = format(startDate, "MMM d");
  const endDateStr = format(endDate, "MMM d");
  return `${startDateStr}, ${startTime} - ${endDateStr}, ${endTime}`;
}
